import React, { useMemo, useState, useRef, useLayoutEffect } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { BANK, BANK_BY_ID, feedersForOutbound } from './mockBank';
import { GATE_POSITIONS } from '../flight-data/gatePositions';
import FlightDetailPanel from './FlightDetailPanel';
import {
  ZOOM_LEVELS, DEFAULT_ZOOM, widthForZoom,
  pxFromMins, hourTicks, halfHourTicks,
  WINDOW_START_MIN, WINDOW_END_MIN,
} from './timeScale';
import { C } from './styles';

const GATE_LANE_H  = 32;          // each lane (stack level) within a gate row
const GATE_PAD     = 4;           // top/bottom padding per gate row
const HEADER_H     = 40;
const SIDEBAR_W    = 100;         // gate column width (content)
const SECTION_HEAD = 26;          // wing-section header height

// Build the ordered list of gates, grouped by wing then numeric order.
function buildGateList() {
  const gates = Object.entries(GATE_POSITIONS).map(([num, pos]) => ({
    gate: `C${num}`,
    num: parseInt(num, 10),
    wing: pos.wing,
    side: pos.side,
  }));
  // West first, then East. Within each, ascending number.
  return gates.sort((a, b) => {
    if (a.wing !== b.wing) return a.wing === 'west' ? -1 : 1;
    return a.num - b.num;
  });
}

// Build gate activities from BANK. One puck per turn = one physical
// aircraft occupancy of the gate. The puck spans [eta, push] so the
// dispatcher sees the full duration the aircraft is at the gate, with
// ETA on the left edge and ETD on the right.
//
// Edge cases:
//   - Originators have no real inbound (the aircraft RON'd here from
//     yesterday). The puck is a short prelude bar leading to push.
//   - Terminators have no outbound (the aircraft RON's tonight). The
//     puck shows the arrival; the overnight hold isn't drawn since
//     ramp ops aren't tracking it.
//
// The outbound STOPS inside each run are connection metadata — where
// the bags ride to — not scheduled events at this concourse. They are
// intentionally NOT rendered here (a single inbound's stops would
// produce dozens of fake gate pucks).
function buildActivities() {
  const fmt = (mins) => {
    const h = Math.floor(mins / 60) % 24;
    const m = mins % 60;
    return `${h < 10 ? '0' : ''}${h}:${m < 10 ? '0' : ''}${m}`;
  };

  const out = [];
  BANK.forEach(inb => {
    const destCity = inb.runs[0]?.stops?.[0]?.destination || '';

    if (inb.isOriginator) {
      // RON aircraft pushing in the morning. Don't draw the overnight
      // sit (it's outside the ops window); just a 30-min lead-in to push.
      out.push({
        id: `turn-${inb.id}`,
        inboundId: inb.id,
        kind: 'originator',
        flightNumber: inb.flightNumber,
        gate: inb.arrivalGate,
        cityFrom: 'RON',
        cityTo:   destCity,
        startMins: inb.etaMins - 30,
        endMins:   inb.etaMins,
        etaTime:   null,
        etdTime:   inb.eta,
      });
      return;
    }
    if (inb.isTerminator) {
      // Aircraft arriving to RON. Show the arrival; the overnight hold
      // isn't relevant to ramp ops.
      out.push({
        id: `turn-${inb.id}`,
        inboundId: inb.id,
        kind: 'terminator',
        flightNumber: inb.flightNumber,
        gate: inb.arrivalGate,
        cityFrom: inb.from,
        cityTo:   'RON',
        startMins: inb.etaMins,
        endMins:   inb.etaMins + 30,
        etaTime:   inb.eta,
        etdTime:   null,
      });
      return;
    }
    // Normal turn — single puck spanning the full gate occupancy.
    out.push({
      id: `turn-${inb.id}`,
      inboundId: inb.id,
      kind: 'turn',
      flightNumber: inb.flightNumber,
      gate: inb.arrivalGate,
      cityFrom: inb.from,
      cityTo:   destCity,
      startMins: inb.etaMins,
      endMins:   inb.pushMins,
      etaTime:   inb.eta,
      etdTime:   fmt(inb.pushMins),
    });
  });

  return out
    .filter(a => a.endMins > WINDOW_START_MIN && a.startMins < WINDOW_END_MIN)
    .map(a => ({
      ...a,
      startMins: Math.max(a.startMins, WINDOW_START_MIN),
      endMins:   Math.min(a.endMins,   WINDOW_END_MIN),
    }));
}

// Group activities by gate and assign each a lane (stack level) so
// overlapping activities don't sit on top of each other.
function groupAndStack(activities) {
  const byGate = new Map();
  activities.forEach(a => {
    if (!byGate.has(a.gate)) byGate.set(a.gate, []);
    byGate.get(a.gate).push(a);
  });
  for (const list of byGate.values()) {
    list.sort((a, b) => a.startMins - b.startMins);
    const laneEnds = []; // laneIdx → end-time of last activity in that lane
    list.forEach(a => {
      let lane = laneEnds.findIndex(end => end <= a.startMins);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(a.endMins);
      } else {
        laneEnds[lane] = a.endMins;
      }
      a.lane = lane;
    });
    list.maxLane = laneEnds.length - 1;
  }
  return byGate;
}

function GateView() {
  const [zoomIdx, setZoomIdx] = useState(DEFAULT_ZOOM);
  // Selected inbound (clicked puck) — opens the detail panel and
  // colors connected gates on the rail.
  const [selectedInboundId, setSelectedInboundId] = useState(null);

  const gates = useMemo(buildGateList, []);
  const activities = useMemo(buildActivities, []);
  const activitiesByGate = useMemo(() => groupAndStack(activities), [activities]);

  // Compute green ("connecting TO") and blue ("connecting FROM") sets
  // when a flight is selected. Two layers each:
  //   *Gates  — drives the row-level tinting on the gate rail
  //   *FlightNumbers — drives the puck-level highlight so the dispatcher
  //                    can spot the specific connecting flight at the
  //                    connecting gate, not just "this gate is involved".
  const {
    greenGates, blueGates, selectedGate,
    greenFlights, blueFlights,
  } = useMemo(() => {
    if (!selectedInboundId) {
      return {
        greenGates: new Set(), blueGates: new Set(),
        greenFlights: new Set(), blueFlights: new Set(),
        selectedGate: null,
      };
    }
    const inb = BANK_BY_ID.get(selectedInboundId);
    if (!inb) {
      return {
        greenGates: new Set(), blueGates: new Set(),
        greenFlights: new Set(), blueFlights: new Set(),
        selectedGate: null,
      };
    }
    const green = new Set();
    const greenFlights = new Set();
    for (const run of inb.runs) {
      for (const stop of run.stops) {
        if (stop.gate && stop.gate !== inb.arrivalGate) green.add(stop.gate);
        if (stop.flightNumber) greenFlights.add(stop.flightNumber);
      }
    }
    const blue = new Set();
    const blueFlights = new Set();
    for (const feeder of feedersForOutbound(inb.flightNumber)) {
      if (feeder.inboundGate && feeder.inboundGate !== inb.arrivalGate) blue.add(feeder.inboundGate);
      if (feeder.inboundFlightNumber) blueFlights.add(feeder.inboundFlightNumber);
    }
    return {
      greenGates: green, blueGates: blue,
      greenFlights, blueFlights,
      selectedGate: inb.arrivalGate,
    };
  }, [selectedInboundId]);

  // Selected flight number (used by GatePuck's dim-others logic).
  const selectedFlight = useMemo(() => {
    if (!selectedInboundId) return null;
    return BANK_BY_ID.get(selectedInboundId)?.flightNumber || null;
  }, [selectedInboundId]);

  // Esc key closes the detail panel.
  React.useEffect(() => {
    if (!selectedInboundId) return;
    const onKey = (e) => { if (e.key === 'Escape') setSelectedInboundId(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedInboundId]);

  const stats = useMemo(() => ({
    turns:        activities.filter(a => a.kind === 'turn').length,
    originators:  activities.filter(a => a.kind === 'originator').length,
    terminators:  activities.filter(a => a.kind === 'terminator').length,
    gates:        gates.length,
  }), [activities, gates]);

  const pxPerMin = ZOOM_LEVELS[zoomIdx].pxPerMin;
  const lanesW   = widthForZoom(zoomIdx);
  const totalW   = SIDEBAR_W + lanesW;

  // Read the rendered offsetLeft of the lane labels box and use it for
  // the absolute-positioned GridOverlay. Avoids box-sizing assumptions.
  const laneRef = useRef(null);
  const [laneOriginX, setLaneOriginX] = useState(SIDEBAR_W);
  useLayoutEffect(() => {
    if (laneRef.current) setLaneOriginX(laneRef.current.offsetLeft);
  }, [zoomIdx, lanesW]);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, position: 'relative' }}>

      {/* Toolbar */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2,
        px: 2, py: 1.25,
        borderBottom: `1px solid ${C.border}`,
        backgroundColor: C.surfaceSolid,
        flexShrink: 0, flexWrap: 'wrap',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <StatChip label="Gates"        value={stats.gates} />
          <StatChip label="Turns"        value={stats.turns}       color={C.accent} />
          <StatChip label="Originators"  value={stats.originators} color={C.accent} />
          <StatChip label="Terminators"  value={stats.terminators} color={C.overnight} />
        </Box>

        <Box sx={{ flex: 1 }} />

        {/* Zoom */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: '0.55rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, mr: 0.5 }}>
            Zoom
          </Typography>
          <Tooltip title="Zoom out">
            <span>
              <IconButton
                size="small"
                onClick={() => setZoomIdx(i => Math.max(0, i - 1))}
                disabled={zoomIdx === 0}
                sx={{ color: C.textSecondary, '&:hover': { color: C.accent }, '&.Mui-disabled': { color: C.textMuted } }}
              >
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography sx={{ fontSize: '0.7rem', color: C.textPrimary, fontWeight: 700, minWidth: 32, textAlign: 'center' }}>
            {ZOOM_LEVELS[zoomIdx].label}
          </Typography>
          <Tooltip title="Zoom in">
            <span>
              <IconButton
                size="small"
                onClick={() => setZoomIdx(i => Math.min(ZOOM_LEVELS.length - 1, i + 1))}
                disabled={zoomIdx === ZOOM_LEVELS.length - 1}
                sx={{ color: C.textSecondary, '&:hover': { color: C.accent }, '&.Mui-disabled': { color: C.textMuted } }}
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Legend */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography sx={{ fontSize: '0.55rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Edge
          </Typography>
          <LegendDot color={C.accent}    label="Turn" />
          <LegendDot color={C.overnight} label="RON" />
        </Box>
      </Box>

      {/* Scroll region */}
      <Box sx={{
        flex: 1,
        overflow: 'auto',
        position: 'relative',
        backgroundColor: C.surfaceSolid,
        '&::-webkit-scrollbar': { height: 8, width: 8 },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(255, 255, 255, 0.15)', borderRadius: 4,
        },
      }}>
        <Box sx={{ width: totalW, minWidth: '100%' }}>

          {/* Header */}
          <Box sx={{
            display: 'flex',
            position: 'sticky', top: 0, zIndex: 5,
            height: HEADER_H,
            backgroundColor: C.surfaceHiSolid,
            borderBottom: `2px solid ${C.borderStrong}`,
          }}>
            <Box sx={{
              width: SIDEBAR_W, flexShrink: 0,
              position: 'sticky', left: 0, zIndex: 6,
              backgroundColor: C.surfaceHiSolid,
              borderRight: `2px solid ${C.borderStrong}`,
              display: 'flex', alignItems: 'center', px: 1.25,
              fontSize: '0.6rem', color: C.textMuted, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 0.5,
            }}>
              Gate
            </Box>
            <Box ref={laneRef} sx={{ position: 'relative', width: lanesW, height: '100%' }}>
              {hourTicks().map(({ minute, label }) => {
                const left = pxFromMins(minute, pxPerMin);
                return (
                  <Box key={minute} sx={{
                    position: 'absolute', left, top: 0, bottom: 0,
                    display: 'flex', alignItems: 'center',
                  }}>
                    <Box sx={{ width: '1px', height: '60%', backgroundColor: C.borderStrong, mr: 0.5 }} />
                    <Typography sx={{
                      fontSize: '0.65rem', fontWeight: 700, color: C.textSecondary,
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Body — gate rows (no wing sections; flat C23 → C67 list). */}
          {gates.map((g) => {
            const list = activitiesByGate.get(g.gate) || [];
            const laneCount = list.length === 0 ? 1 : (list.maxLane + 1);
            const rowH = GATE_PAD * 2 + laneCount * GATE_LANE_H + (laneCount - 1) * 2;
            return (
              <React.Fragment key={g.gate}>
                <GateRow
                  gate={g}
                  activities={list}
                  rowH={rowH}
                  pxPerMin={pxPerMin}
                  lanesW={lanesW}
                  selectedFlight={selectedFlight}
                  selectedInboundId={selectedInboundId}
                  onSelectInbound={setSelectedInboundId}
                  highlight={
                    selectedGate === g.gate ? 'selected'
                    : greenGates.has(g.gate) ? 'green'
                    : blueGates.has(g.gate) ? 'blue'
                    : null
                  }
                  greenFlights={greenFlights}
                  blueFlights={blueFlights}
                />
              </React.Fragment>
            );
          })}

          <GridOverlay
            pxPerMin={pxPerMin}
            lanesW={lanesW}
            bodyTop={HEADER_H}
            laneLeft={laneOriginX}
          />
        </Box>
      </Box>

      {/* Flight detail panel — slides up from the bottom when a puck
          is clicked. Esc closes. */}
      {selectedInboundId && (
        <FlightDetailPanel
          inboundId={selectedInboundId}
          onClose={() => setSelectedInboundId(null)}
        />
      )}
    </Box>
  );
}

// ── Sub-components ────────────────────────────────────────────────────

const GateRow = React.memo(function GateRow({
  gate, activities, rowH, pxPerMin, lanesW, selectedFlight,
  selectedInboundId, onSelectInbound, highlight,
  greenFlights, blueFlights,
}) {
  // Highlight palette: green = "connecting TO" (this flight's bags
  // are going there); blue = "connecting FROM" (bags coming TO this
  // flight come from here); selected = the clicked flight's own gate.
  const highlightBg = highlight === 'selected' ? 'rgba(249, 182, 18, 0.10)'
                    : highlight === 'green'    ? 'rgba(76, 175, 80, 0.10)'
                    : highlight === 'blue'     ? 'rgba(2, 119, 189, 0.10)'
                    : 'transparent';
  const highlightSidebar = highlight === 'selected' ? 'rgba(249, 182, 18, 0.22)'
                         : highlight === 'green'    ? 'rgba(76, 175, 80, 0.22)'
                         : highlight === 'blue'     ? 'rgba(2, 119, 189, 0.22)'
                         : null;
  const highlightAccent = highlight === 'selected' ? C.accent
                        : highlight === 'green'    ? C.ok
                        : highlight === 'blue'     ? C.cold
                        : null;
  return (
    <Box sx={{
      display: 'flex',
      height: rowH,
      borderBottom: `1px solid ${C.border}`,
      backgroundColor: highlightBg,
      '&:hover': {
        backgroundColor: highlight ? highlightBg : 'rgba(255, 255, 255, 0.03)',
        '& .gate-sidebar': { backgroundColor: highlightSidebar || 'rgba(255, 255, 255, 0.05)' },
      },
    }}>
      <Box className="gate-sidebar" sx={{
        width: SIDEBAR_W, flexShrink: 0,
        position: 'sticky', left: 0, zIndex: 3,
        borderRight: `2px solid ${C.borderStrong}`,
        borderLeft: highlightAccent ? `3px solid ${highlightAccent}` : 'none',
        backgroundColor: highlightSidebar || 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background-color 60ms, border-color 60ms',
      }}>
        <Typography sx={{
          fontSize: '0.85rem', color: C.textPrimary, fontWeight: 700,
          fontVariantNumeric: 'tabular-nums', letterSpacing: 0.4,
        }}>
          {gate.gate}
        </Typography>
      </Box>

      <Box sx={{ position: 'relative', width: lanesW, height: '100%' }}>
        {activities.map((a) => {
          const left  = pxFromMins(a.startMins, pxPerMin);
          const width = Math.max(40, (a.endMins - a.startMins) * pxPerMin);
          const top   = GATE_PAD + a.lane * (GATE_LANE_H + 2);
          const isSelected = selectedInboundId && selectedInboundId === a.inboundId;
          // Connection flag: this specific puck is one of the flights
          // that connects with the currently-selected flight.
          const connection = greenFlights?.has(a.flightNumber) ? 'green'
                           : blueFlights?.has(a.flightNumber)  ? 'blue'
                           : null;
          // Dim only non-related pucks. Selected, green, and blue pucks
          // stay bright; everything else fades back so the connections
          // pop visually.
          const dimmed = Boolean(selectedInboundId) && !isSelected && !connection;
          return (
            <GatePuck
              key={a.id}
              left={left}
              top={top}
              width={width}
              activity={a}
              selected={isSelected}
              dimmed={dimmed}
              connection={connection}
              onClick={() => onSelectInbound(isSelected ? null : a.inboundId)}
            />
          );
        })}
      </Box>
    </Box>
  );
});

function GatePuck({ left, top, width, activity, selected, dimmed, connection, onClick }) {
  // Connection takes precedence over the base kind tint — if this puck
  // is the specific flight feeding/receiving from the currently-selected
  // flight, color the whole puck green/blue so it pops on its row.
  const connectionColor = connection === 'green' ? C.ok
                        : connection === 'blue'  ? C.cold
                        : null;
  const baseAccent = activity.kind === 'terminator' ? C.overnight : C.accent;
  const accent = connectionColor || baseAccent;
  const num = (activity.flightNumber || '').replace(/^WN\s*/, '');
  const tight = width < 110;  // collapse middle text on short pucks

  // Tint the puck body when it's a connection so the connection reads
  // at a glance even on a busy gate row.
  const bg = connection === 'green' ? 'rgba(76, 175, 80, 0.22)'
           : connection === 'blue'  ? 'rgba(2, 119, 189, 0.22)'
           : 'rgba(20, 20, 25, 0.78)';

  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'absolute',
        top, left, width,
        height: GATE_LANE_H,
        backgroundColor: bg,
        border: `1px solid ${selected ? C.accent : connectionColor ? `${connectionColor}AA` : C.borderStrong}`,
        borderLeft: `4px solid ${accent}`,
        borderRadius: 1,
        boxShadow: selected
          ? `0 0 0 1px ${C.accent}, 0 4px 12px rgba(0, 0, 0, 0.5)`
          : connectionColor
            ? `0 0 0 1px ${connectionColor}55, 0 4px 10px ${connectionColor}33`
            : '0 1px 3px rgba(0, 0, 0, 0.4)',
        opacity: dimmed ? 0.35 : 1,
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 0.6, px: 0.75,
        overflow: 'hidden',
        transition: 'opacity 90ms, transform 90ms, box-shadow 90ms, background-color 90ms',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.55)',
          zIndex: 2,
        },
      }}
    >
      {/* ETA on the left edge — null for originators (RON, no inbound) */}
      {activity.etaTime ? (
        <Typography sx={{
          fontSize: '0.62rem', color: C.textSecondary, fontWeight: 700,
          fontVariantNumeric: 'tabular-nums', lineHeight: 1, flexShrink: 0,
        }}>
          {activity.etaTime}
        </Typography>
      ) : null}

      {/* Flight number */}
      <Typography sx={{
        fontSize: '0.72rem', fontWeight: 700, color: C.textPrimary,
        fontVariantNumeric: 'tabular-nums', lineHeight: 1, flexShrink: 0,
      }}>
        {num}
      </Typography>

      {/* From → To — hidden on short pucks to keep them legible */}
      {!tight && (
        <Typography sx={{
          fontSize: '0.6rem', color: C.textMuted, lineHeight: 1,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {activity.cityFrom} → {activity.cityTo}
        </Typography>
      )}

      <Box sx={{ flex: 1 }} />

      {/* ETD on the right edge — null for terminators (RON, no outbound) */}
      {activity.etdTime ? (
        <Typography sx={{
          fontSize: '0.62rem', color: C.textSecondary, fontWeight: 700,
          fontVariantNumeric: 'tabular-nums', lineHeight: 1, flexShrink: 0,
        }}>
          {activity.etdTime}
        </Typography>
      ) : null}
    </Box>
  );
}

function GridOverlay({ pxPerMin, lanesW, bodyTop, laneLeft }) {
  const halves = halfHourTicks();
  const hours  = hourTicks();
  return (
    <Box sx={{
      position: 'absolute',
      top: bodyTop, bottom: 0,
      // Mirror the header lane-labels box's actual offsetLeft.
      left: laneLeft,
      width: lanesW,
      pointerEvents: 'none',
      zIndex: 1,
    }}>
      {hours.map(({ minute }) => (
        <Box key={`h-${minute}`} sx={{
          position: 'absolute', top: 0, bottom: 0,
          left: pxFromMins(minute, pxPerMin),
          width: '1px',
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
        }} />
      ))}
      {halves.map(m => (
        <Box key={`hh-${m}`} sx={{
          position: 'absolute', top: 0, bottom: 0,
          left: pxFromMins(m, pxPerMin),
          width: '1px',
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
        }} />
      ))}
    </Box>
  );
}

function StatChip({ label, value, color = C.textPrimary }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'baseline', gap: 0.4,
      px: 1, py: 0.3,
      backgroundColor: C.surfaceAlt,
      border: `1px solid ${C.border}`,
      borderRadius: 1,
    }}>
      <Typography sx={{ fontSize: '0.85rem', color, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: '0.55rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
    </Box>
  );
}

function LegendDot({ color, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color }} />
      <Typography sx={{ fontSize: '0.55rem', color: C.textMuted }}>{label}</Typography>
    </Box>
  );
}

export default GateView;
